/**
 * What each Support at Home strategy IS, how it works, and what to weigh up — the ONE authoritative
 * narrative library, keyed by the engine's `config.type`.
 *
 * Two voices, one truth:
 *  - `what` / `how` / `consider` — the client-facing voice, rendered live in the insight panel when
 *    a suggestion-engine card is selected;
 *  - `soa` — the formal advice-document voice (benefits / risks / suitability), rendered in the
 *    Statement of Advice.
 * They live in one record per strategy so the screen and the SOA can never tell the client
 * different stories. The mechanics must match what the engine actually models (see
 * server/src/rest/calculation/suggestionEngine/supportAtHome.ts); if a strategy's modelling
 * changes, its explanation changes with it — and a test pins this library against the engine's
 * authoritative type list, so a new strategy type cannot ship without narrative.
 */
import { formatCurrency } from '../../utils/index.ts';
import { FUNERAL_BOND_LIMIT } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';

export interface SahStrategySoaNarrative {
    /** Formal one-paragraph description for the SOA body. */
    description: string;
    benefits: string[];
    risks: string[];
    /** Who this strategy tends to suit — the adviser's suitability framing. */
    suitability: string;
}

export interface SahStrategyExplainer {
    what: string;
    how: string[];
    consider: string[];
    soa: SahStrategySoaNarrative;
}

export const SAH_STRATEGY_EXPLAINERS: Record<string, SahStrategyExplainer> = {
    doNothing: {
        what: 'Keep everything as it is — no restructuring, no borrowing, no gifts. This is the baseline every other option is measured against.',
        how: [
            'Your Age Pension, contribution rates and cash flow stay exactly as they are today.',
            'It ranks well when your position is already strong — sometimes the honest answer is that the clever options don’t beat it.',
        ],
        consider: [
            'If your cash flow is negative, doing nothing means drawing down savings each year — check how long they last in the projections.',
        ],
        soa: {
            description: 'Retain the current financial position without restructuring assets, borrowing against the home, or gifting. This baseline is the benchmark against which every alternative strategy in this document was measured.',
            benefits: [
                'No transaction costs, no loss of liquidity and no irreversible commitments.',
                'Your position remains fully flexible for future decisions, including future care needs.',
            ],
            risks: [
                'If annual cash flow is negative, savings are drawn down each year; the projections in this document show how the position evolves.',
                'Opportunities to improve Age Pension entitlement or reduce contribution rates remain unused.',
            ],
            suitability: 'Appropriate where the existing position already meets your income and estate objectives, or where the modelled benefit of the alternatives does not justify their costs and risks.',
        },
    },
    gifting: {
        what: 'Give money to family now, within Centrelink’s allowed limits, so it stops counting against your Age Pension.',
        how: [
            'Centrelink allows gifts of up to $10,000 per financial year (and $30,000 over five years) without penalty.',
            'The gifted amount leaves your assessable assets, which can lift your Age Pension and lower your contribution rates.',
        ],
        consider: [
            'You lose access to this money permanently — it’s a real gift, not an accounting move.',
            'Anything above the limits is treated as a “deprived asset” and still counted against you for five years.',
            'Your estate is smaller by the gifted amount (though the family receives it earlier instead).',
        ],
        soa: {
            description: 'Gift capital to family members within Centrelink’s allowable disposal limits ($10,000 per financial year, $30,000 over a rolling five-year period), reducing assessable assets and deemed income.',
            benefits: [
                'Reduces assessable assets immediately, which can increase Age Pension entitlement and lower Support at Home contribution rates.',
                'Brings forward an intended bequest, allowing family to benefit while you can see it.',
            ],
            risks: [
                'Gifted funds are permanently inaccessible to you — this cannot be unwound if your circumstances change.',
                'Amounts above the allowable limits are assessed as deprived assets for five years from the date of the gift, delivering no means-test benefit.',
                'Your estate is reduced by the amount gifted.',
            ],
            suitability: 'Suited to clients with surplus liquid assets, a genuine intention to benefit family, and sufficient remaining reserves for contingencies including future care costs.',
        },
    },
    funeralBonds: {
        what: 'Set money aside in a funeral bond — an investment Centrelink exempts from the assets test, up to the allowable limit.',
        how: [
            'The bond amount leaves your assessable assets, which can lift your Age Pension and lower your contribution rates.',
            'It also quietly pre-funds a real future cost.',
        ],
        consider: [
            'The money is locked until it’s needed for funeral expenses — you can’t draw it back out.',
            `Only amounts up to the allowable limit (${formatCurrency(FUNERAL_BOND_LIMIT)} per person) are exempt.`,
        ],
        soa: {
            description: 'Invest in a funeral bond up to the allowable exempt limit. The bond is excluded from the Centrelink assets test and pre-funds funeral expenses.',
            benefits: [
                'Reduces assessable assets within the exempt limit, which can increase Age Pension entitlement and lower contribution rates.',
                'Pre-funds a certain future expense, removing it from the estate’s future obligations.',
            ],
            risks: [
                'Funds are locked until required for funeral expenses and cannot be redeemed for other purposes.',
                'Only amounts up to the allowable limit per person are exempt; contributions above it gain no means-test benefit.',
                'Investment returns within funeral bonds are generally modest.',
            ],
            suitability: 'Suited to clients who intend to pre-fund funeral costs in any case and hold sufficient liquid reserves for other needs.',
        },
    },
    annuity: {
        what: 'Convert a lump sum into a lifetime income stream. Centrelink assesses only 60% of the purchase price as an asset (for the first five years, or until age 85 if that is later, and 30% after that), so your assessable assets fall while you keep receiving the money as income.',
        how: [
            'The purchase price leaves your investments; regular annuity payments arrive for life.',
            'The 40% asset-test discount can lift your Age Pension and lower your contribution rates.',
            'Only a portion of each payment counts as income under the income test.',
        ],
        consider: [
            'The purchase is generally irreversible — you trade access to the lump sum for the income.',
            'Your estate is usually lower: depending on the product, what’s left for the family can be much less than the unspent lump sum would have been.',
            'Product terms (indexation, death benefits, withdrawal values) vary a lot — the model uses standard assumptions.',
        ],
        soa: {
            description: 'Purchase a lifetime annuity with a portion of investible assets. Under current rules only 60% of the purchase price is assessed under the assets test (for the first five years, or until age 85 if that is later, and 30% after that), and only a portion of each payment is assessed as income.',
            benefits: [
                'Immediate 40% assets-test discount on the purchase price, which can increase Age Pension entitlement and lower contribution rates.',
                'Guaranteed income for life, independent of market performance.',
                'Concessional income-test treatment of payments.',
            ],
            risks: [
                'The purchase is generally irreversible; access to the capital is exchanged for the income stream.',
                'Estate value is typically reduced — death benefits vary by product and can be substantially less than the unspent capital would have been.',
                'Product features (indexation, guarantee periods, withdrawal values) vary between providers; the modelling uses standard assumptions and the chosen product must be assessed on its own terms.',
            ],
            suitability: 'Suited to clients seeking certainty of income and improved means-test outcomes who can commit capital permanently, with sufficient assets retained outside the annuity for liquidity.',
        },
    },
    renovate: {
        what: 'Spend savings improving the home you live in. Your home is exempt from the assets test, so money moved into it stops counting against your Age Pension.',
        how: [
            'The renovation amount leaves your bank account (assessable) and becomes home value (exempt).',
            'Lower assessable assets can lift your Age Pension and lower your contribution rates.',
            'You keep the value — it’s in the house rather than the bank.',
        ],
        consider: [
            'The money is no longer liquid: you can’t spend home equity at the shops, and it’s only realised if the home is sold.',
            'A renovation doesn’t always add its full cost to the home’s market value.',
        ],
        soa: {
            description: 'Apply liquid capital to improvements to the principal home. The principal home is exempt from the assets test, so capital moved into it ceases to be assessable.',
            benefits: [
                'Reduces assessable assets, which can increase Age Pension entitlement and lower contribution rates.',
                'Value is retained within your estate, in the home, and the improvements may also support remaining at home longer — the core objective of Support at Home.',
            ],
            risks: [
                'Liquidity is permanently reduced; home equity cannot fund day-to-day expenses without borrowing or selling.',
                'Renovations do not reliably add their full cost to market value.',
                'If residential aged care is later required, means-tested treatment of the home differs and should be re-modelled at that time.',
            ],
            suitability: 'Suited to homeowners intending to remain at home long-term, with improvements that genuinely serve that goal, and sufficient liquidity retained for contingencies.',
        },
    },
    upgrade: {
        what: 'Sell your current home and buy a more expensive one, moving savings into the exempt family home.',
        how: [
            'The extra purchase cost leaves your assessable assets and becomes exempt home value.',
            'Lower assessable assets can lift your Age Pension and lower your contribution rates.',
        ],
        consider: [
            'Buying, selling and moving costs are real money gone.',
            'More of your wealth is tied up in one illiquid asset.',
        ],
        soa: {
            description: 'Sell the current principal home and purchase a more valuable one, converting assessable capital into exempt principal-home value.',
            benefits: [
                'Reduces assessable assets, which can increase Age Pension entitlement and lower contribution rates.',
                'Wealth is retained in the estate, in the new home.',
            ],
            risks: [
                'Transaction costs (agent fees, stamp duty, moving) are permanent losses.',
                'Concentration of wealth in a single illiquid asset increases exposure to the property market and reduces financial flexibility.',
                'The disruption of moving is material at older ages.',
            ],
            suitability: 'Suited to clients already intending to move, where the new home better serves long-term needs, and liquidity after the transaction remains adequate.',
        },
    },
    downsize: {
        what: 'Sell your home and buy a cheaper one, freeing up equity as cash.',
        how: [
            'The difference between the sale and the new home lands in your bank account, boosting your available money and income.',
            'Surplus proceeds become assessable and are deemed to earn income — so this usually REDUCES Age Pension while it improves cash on hand.',
        ],
        consider: [
            'A lower Age Pension can also mean HIGHER Support at Home contribution rates — the model shows the net effect.',
            'Downsizer super contributions can shelter some proceeds, but super is assessable again once you’re over pension age.',
            'Selling and moving costs, and the upheaval of moving, are part of the price.',
        ],
        soa: {
            description: 'Sell the principal home and purchase a lower-value property, releasing home equity as accessible capital.',
            benefits: [
                'Substantially improves liquidity and investment income, strengthening capacity to fund care and living costs.',
                'A home better matched to current needs can itself support remaining at home longer.',
            ],
            risks: [
                'Surplus proceeds become assessable and deemed, which typically reduces Age Pension entitlement and can raise Support at Home contribution rates — the modelling in this document shows the net effect.',
                'Downsizer superannuation contributions shelter proceeds only while under Age Pension age; superannuation is assessable once over it.',
                'Transaction and moving costs are permanent losses, and relocation is disruptive.',
            ],
            suitability: 'Suited to clients whose current home no longer fits their needs or whose liquidity is insufficient to fund care sustainably, and who accept the means-test consequences quantified in this document.',
        },
    },
    debtRepayment: {
        what: 'Use savings to clear a debt. Money you use to pay off a loan is spent, but the loan’s interest stops and your assessable assets fall.',
        how: [
            'The repayment leaves your bank account; the loan (and its repayments) disappear from your cash flow.',
            'Lower assessable assets can lift your Age Pension and lower your contribution rates.',
        ],
        consider: [
            'The money is gone from your accessible savings — make sure enough liquidity remains for emergencies.',
            'This wins when the loan’s interest rate is high relative to what your savings earn — the ranking already weighs that.',
        ],
        soa: {
            description: 'Apply liquid capital to extinguish an existing debt, removing its interest cost and repayments from cash flow while reducing assessable assets.',
            benefits: [
                'Immediate cash-flow improvement equal to the former repayments; a guaranteed after-tax “return” equal to the loan’s interest rate.',
                'Reduces assessable assets, which can increase Age Pension entitlement and lower contribution rates.',
            ],
            risks: [
                'Liquidity is reduced by the repayment amount; sufficient reserves must remain for contingencies.',
                'Where the loan rate is low relative to investment returns, repayment can modestly reduce long-run wealth — the comparison in this document quantifies the trade for your actual loan.',
            ],
            suitability: 'Suited to clients carrying debt at interest rates above their realistic investment return, with liquid assets comfortably exceeding the repayment.',
        },
    },
    reverseMortgage: {
        what: 'Borrow against your home through a commercial lender while continuing to live in it.',
        how: [
            'A lump sum (or drawdowns) arrives as cash, improving your available money and cash flow.',
            'Nothing is repaid month to month — interest is added to the loan and repaid when the home is eventually sold.',
        ],
        consider: [
            'Interest compounds on the growing balance, so your estate shrinks faster the longer it runs.',
            'The lump sum you hold becomes an assessable asset and is deemed — it can reduce your Age Pension.',
            'Australian law guarantees you can never owe more than the home is worth (no negative equity).',
        ],
        soa: {
            description: 'Establish a commercial reverse mortgage against the principal home, drawing a lump sum (or scheduled drawdowns) with no servicing obligation; interest capitalises and the loan is repaid when the home is sold or from the estate.',
            benefits: [
                'Unlocks home equity as accessible funds without selling or moving.',
                'No repayment obligation during the loan; statutory no-negative-equity protection applies.',
            ],
            risks: [
                'Interest compounds on a growing balance, materially reducing home equity and estate value over time — quantified in the projections in this document.',
                'Lump-sum proceeds held in financial assets are assessable and deemed, which can reduce Age Pension entitlement.',
                'Commercial interest rates are significantly higher than the government scheme (HEAS); product terms vary and require independent comparison.',
            ],
            suitability: 'Suited to clients needing a larger or more flexible draw than HEAS permits, who accept the compounding cost to the estate and have considered HEAS first.',
        },
    },
    heas: {
        what: 'The Home Equity Access Scheme — the government’s own version of a reverse mortgage, paid as regular fortnightly amounts on top of your pension.',
        how: [
            'Fortnightly payments supplement your income; the balance is secured against your home at the government’s rate (currently 3.95%, well below commercial reverse-mortgage rates).',
            'HEAS payments are NOT counted as income for the pension means test, so taking them doesn’t reduce your Age Pension.',
            'You can stop, start or repay at any time, and the no-negative-equity guarantee applies.',
        ],
        consider: [
            'It’s still a loan: interest compounds, so your home equity — and your estate — reduce over time. The Legacy figure on the card shows that trade-off.',
            'Combined pension + HEAS payments are capped at 150% of the maximum pension rate.',
        ],
        soa: {
            description: 'Access the government’s Home Equity Access Scheme, receiving regular fortnightly payments secured against the principal home at the scheme’s interest rate (currently 3.95% p.a., compounding fortnightly).',
            benefits: [
                'Supplements income at the lowest available home-equity borrowing rate, with government administration.',
                'Payments are not assessed as income for the Age Pension means test and do not affect pension entitlement.',
                'Fully flexible: payments can be stopped, started or repaid at any time, with statutory no-negative-equity protection.',
            ],
            risks: [
                'Interest compounds against home equity, reducing the estate over time — the projections in this document quantify this for the modelled payment level.',
                'Combined Age Pension and HEAS payments are capped at 150% of the maximum pension rate, limiting the achievable draw.',
                'The scheme’s interest rate is variable and set by the government; it may change.',
            ],
            suitability: 'Suited to homeowners needing a modest, regular income supplement who wish to remain at home, accept a gradual reduction in estate value, and value flexibility and consumer protection over the larger sums available commercially.',
        },
    },
};

// The engine emits `increaseHomeEquity` as an alias of renovate (same modelling, generated name
// "Increase home equity ($X)") — both must resolve or that card gets no explanation.
SAH_STRATEGY_EXPLAINERS.increaseHomeEquity = SAH_STRATEGY_EXPLAINERS.renovate;
